import { DeleteOutlined, EditOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons';
import {
    Button,
    Form,
    Input,
    Modal,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import useRequest from '../hooks/useRequest';

const { Title } = Typography;

interface UserData {
    id: number;
    username: string;
    role: string;
    isActive: boolean;
    email: string;
    lastLogin: string | null;
    categoryId: number | null;
    categoryName: string | null;
}

/** The roles the backend assigns. `ADMIN` is compared exactly and is never restricted by a category. */
const ROLE_ADMIN = 'ADMIN';
const ROLE_USER = 'USER';
const ROLE_LABEL_KEYS: Record<string, string> = {
    [ROLE_ADMIN]: 'configuration.users.roleAdmin',
    [ROLE_USER]: 'configuration.users.roleUser',
};
const ROLES = [ROLE_ADMIN, ROLE_USER];

interface CategoryOption {
    id: number;
    name: string;
}

const UserManagement = () => {
    const { t } = useTranslation();
    const { user: currentUser } = useAuth();
    const request = useRequest();
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [categories, setCategories] = useState<CategoryOption[]>([]);
    const [form] = Form.useForm();
    const selectedRole = Form.useWatch('role', form);

    const roleLabel = (role: string): string =>
        ROLE_LABEL_KEYS[role] ? t(ROLE_LABEL_KEYS[role]) : role;

    const renderCategory = (record: UserData) => {
        if (record.role === ROLE_ADMIN) {
            return <Tag>{t('configuration.users.allPages')}</Tag>;
        }
        if (record.categoryName) {
            return <Tag color="geekblue">{record.categoryName}</Tag>;
        }
        return <Tag color="red">{t('configuration.users.noCategory')}</Tag>;
    };

    const loadUsers = useCallback(async () => {
        try {
            setLoading(true);
            const response = await request<{ data: UserData[] }>({
                path: '/users',
                method: 'GET',
            });
            setUsers(response.data ?? []);
        } catch (error) {
            console.error('Error loading users:', error);
            message.error(t('configuration.users.loadError'));
        } finally {
            setLoading(false);
        }
    }, [request, t]);

    const loadCategories = useCallback(async () => {
        try {
            const response = await request<{ data: CategoryOption[] }>({
                path: '/user-categories',
                method: 'GET',
            });
            setCategories(response.data ?? []);
        } catch (error) {
            console.error('Error loading user categories:', error);
            message.error(t('configuration.users.categoriesLoadError'));
        }
    }, [request, t]);

    useEffect(() => {
        void loadUsers();
        void loadCategories();
    }, [loadUsers, loadCategories]);

    const handleCreate = () => {
        setEditingUser(null);
        form.resetFields();
        form.setFieldsValue({ isActive: true, role: ROLE_USER });
        setModalVisible(true);
    };

    const handleEdit = (record: UserData) => {
        setEditingUser(record);
        form.setFieldsValue({
            username: record.username,
            role: record.role,
            email: record.email,
            isActive: record.isActive,
            categoryId: record.categoryId ?? undefined,
            password: '',
        });
        setModalVisible(true);
    };

    const handleDelete = (record: UserData) => {
        if (Number(currentUser?.id) === record.id) {
            message.warning(t('configuration.users.cannotDeleteSelf'));
            return;
        }

        Modal.confirm({
            title: t('configuration.users.deleteUser'),
            content: t('configuration.users.confirmDelete', { username: record.username }),
            okType: 'danger',
            onOk: async () => {
                try {
                    await request({
                        path: `/users/${record.id}`,
                        method: 'DELETE',
                    });
                    message.success(t('configuration.users.deleteSuccess'));
                    void loadUsers();
                } catch (error) {
                    console.error('Error deleting user:', error);
                    message.error(t('configuration.users.deleteError'));
                }
            },
        });
    };

    const handleSubmit = async (values: {
        username: string;
        password?: string;
        role: string;
        email?: string;
        isActive: boolean;
        categoryId?: number;
    }) => {
        // An administrator is never restricted, so no category is kept for one.
        const categoryId = values.role === ROLE_ADMIN ? null : (values.categoryId ?? null);
        try {
            if (editingUser) {
                const payload: Record<string, unknown> = {
                    username: values.username,
                    role: values.role,
                    email: values.email ?? '',
                    isActive: values.isActive,
                    categoryId,
                };
                if (values.password) {
                    payload.password = values.password;
                }
                await request({
                    path: `/users/${editingUser.id}`,
                    method: 'PUT',
                    params: payload,
                });
                message.success(t('configuration.users.updateSuccess'));
            } else {
                await request({
                    path: '/users',
                    method: 'POST',
                    params: {
                        username: values.username,
                        password: values.password,
                        role: values.role,
                        email: values.email ?? '',
                        isActive: values.isActive,
                        categoryId,
                    },
                });
                message.success(t('configuration.users.createSuccess'));
            }
            setModalVisible(false);
            form.resetFields();
            void loadUsers();
        } catch (error) {
            const errorMessage = editingUser
                ? t('configuration.users.updateError')
                : t('configuration.users.createError');
            console.error(errorMessage, error);
            message.error(errorMessage);
        }
    };

    const columns: ColumnsType<UserData> = [
        {
            title: t('configuration.users.username'),
            dataIndex: 'username',
            key: 'username',
            sorter: (a, b) => a.username.localeCompare(b.username),
        },
        {
            title: t('configuration.users.role'),
            dataIndex: 'role',
            key: 'role',
            render: (role: string) => <Tag color="blue">{roleLabel(role)}</Tag>,
            filters: ROLES.map(role => ({ text: roleLabel(role), value: role })),
            onFilter: (value, record) => record.role === value,
        },
        {
            title: t('configuration.users.category'),
            key: 'category',
            render: (_: unknown, record: UserData) => renderCategory(record),
            filters: categories.map(category => ({ text: category.name, value: category.id })),
            onFilter: (value, record) => record.categoryId === value,
        },
        {
            title: t('configuration.users.email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: t('configuration.users.isActive'),
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'green' : 'red'}>
                    {isActive ? t('configuration.users.isActive') : 'Inactive'}
                </Tag>
            ),
            filters: [
                { text: t('configuration.users.isActive'), value: true },
                { text: 'Inactive', value: false },
            ],
            onFilter: (value, record) => record.isActive === value,
        },
        {
            title: t('configuration.users.lastLogin'),
            dataIndex: 'lastLogin',
            key: 'lastLogin',
            render: (date: string | null) => (date ? new Date(date).toLocaleString() : '-'),
            sorter: (a, b) => {
                if (!a.lastLogin) return -1;
                if (!b.lastLogin) return 1;
                return new Date(a.lastLogin).getTime() - new Date(b.lastLogin).getTime();
            },
        },
        {
            title: t('configuration.users.actions'),
            key: 'actions',
            render: (_: unknown, record: UserData) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    />
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        disabled={Number(currentUser?.id) === record.id}
                    />
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Title level={4}>
                    <UserOutlined style={{ marginRight: 8 }} />
                    {t('configuration.users.title')}
                </Title>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    {t('configuration.users.addUser')}
                </Button>
            </div>

            <Table<UserData>
                columns={columns}
                dataSource={users}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                locale={{ emptyText: t('configuration.users.noUsers') }}
                size="small"
            />

            <Modal
                title={
                    editingUser
                        ? t('configuration.users.editUser')
                        : t('configuration.users.addUser')
                }
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                destroyOnHidden
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="username"
                        label={t('configuration.users.username')}
                        rules={[
                            {
                                required: true,
                                message: t('configuration.users.usernameRequired'),
                            },
                        ]}
                    >
                        <Input placeholder={t('configuration.users.usernamePlaceholder')} />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={t('configuration.users.password')}
                        // Editing may leave it empty, but never short: the server refuses that.
                        rules={[
                            ...(editingUser
                                ? []
                                : [
                                      {
                                          required: true,
                                          message: t('configuration.users.passwordRequired'),
                                      },
                                  ]),
                            { min: 6, message: t('settings.passwordMinLength') },
                        ]}
                        extra={
                            editingUser ? t('configuration.users.passwordLeaveEmpty') : undefined
                        }
                    >
                        <Input.Password
                            placeholder={t('configuration.users.passwordPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="role"
                        label={t('configuration.users.role')}
                        rules={[{ required: true, message: t('configuration.users.roleRequired') }]}
                    >
                        <Select
                            placeholder={t('configuration.users.selectRole')}
                            data-testid="user-role"
                        >
                            {ROLES.map(role => (
                                <Select.Option key={role} value={role}>
                                    {roleLabel(role)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {selectedRole === ROLE_ADMIN ? (
                        <Typography.Paragraph type="secondary">
                            {t('configuration.users.adminAllPages')}
                        </Typography.Paragraph>
                    ) : (
                        <Form.Item
                            name="categoryId"
                            label={t('configuration.users.category')}
                            extra={t('configuration.users.categoryHint')}
                            rules={[
                                {
                                    required: true,
                                    message: t('configuration.users.categoryRequired'),
                                },
                            ]}
                        >
                            <Select
                                placeholder={t('configuration.users.selectCategory')}
                                options={categories.map(category => ({
                                    value: category.id,
                                    label: category.name,
                                }))}
                                data-testid="user-category"
                            />
                        </Form.Item>
                    )}

                    <Form.Item name="email" label={t('configuration.users.email')}>
                        <Input
                            type="email"
                            placeholder={t('configuration.users.emailPlaceholder')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="isActive"
                        label={t('configuration.users.isActive')}
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingUser
                                    ? t('common.save')
                                    : t('configuration.users.addUser')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setModalVisible(false);
                                    form.resetFields();
                                }}
                            >
                                {t('common.cancel')}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default UserManagement;
